import React, { useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import { AreaChart } from 'components';
import { formatters } from 'services';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import {
  fetchRealtimeBenchmarkPreview,
  RealtimeBenchmarkPreview,
} from './realtimeBenchmarkPreview';

interface Props {
  sessionId: number;
  benchmarkKey: string;
  hasSaved: boolean;
}

/** Separate request state: the historical preview/store is never read or updated. */
const RealtimeBenchmarkPreviewV2: React.FC<Props> = ({
  sessionId,
  benchmarkKey,
  hasSaved,
}) => {
  const requestKey = `${sessionId}:${benchmarkKey}`;
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data?: RealtimeBenchmarkPreview;
    error?: boolean;
  }>();

  useEffect(() => {
    if (!hasSaved) return;
    const controller = new AbortController();
    setState({ key: requestKey });
    fetchRealtimeBenchmarkPreview(sessionId, controller.signal).then(
      data => {
        if (!controller.signal.aborted) setState({ key: requestKey, data });
      },
      () => {
        if (!controller.signal.aborted)
          setState({ key: requestKey, error: true });
      },
    );
    return () => controller.abort();
  }, [sessionId, requestKey, hasSaved, retry]);

  // A new session/basket cannot flash the preceding request's chart before its
  // effect runs, nor can a slower obsolete response replace the current one.
  const current = state?.key === requestKey ? state : undefined;
  const preview = hasSaved ? current?.data : undefined;
  const loading = hasSaved && !current?.data && !current?.error;
  let message: string | undefined;
  if (!hasSaved || preview?.status === 'empty') {
    message =
      'Save a benchmark basket to preview its last three months of EOD returns.';
  } else if (current?.error) {
    message = 'Could not load the benchmark return.';
  } else if (preview?.status === 'unavailable') {
    message = `No usable EOD price history for: ${preview.unavailable_components.join(', ')}.`;
  }

  const data = (preview?.composite ?? []).map((y, index) => ({
    x: Date.parse(`${preview.dates[index]}T00:00:00Z`),
    y,
  }));
  const additionalLines =
    (preview?.components.length ?? 0) > 1
      ? preview.components.map(component => ({
          name: component.name,
          data: component.returns.map((y, index) => ({
            x: Date.parse(`${preview.dates[index]}T00:00:00Z`),
            y,
          })),
        }))
      : [];
  const lateStart =
    preview?.dates.length > 0 && preview.dates[0] > preview.window_start;
  const conflicts = preview?.components.some(
    c => c.conflicting_dates.length > 0,
  );

  return (
    <SettingsCard
      title="Benchmark return"
      className="bmv2-card bmv2-flexbody"
      extra={
        <span className="bmv2-pill-stat">
          {preview
            ? `${preview.window_start} → ${preview.window_end}`
            : 'Last 3 months · EOD'}
        </span>
      }
      footer={
        preview?.status === 'ready' ? (
          <span>
            EOD prices; missing dates use the last available price.
            {lateStart ? ` Return series starts ${preview.dates[0]}.` : ''}
            {conflicts
              ? ' Conflicting prices also use the preceding price.'
              : ''}
          </span>
        ) : undefined
      }
    >
      {message || loading ? (
        <div className="bmv2-preview-empty">
          {loading ? (
            <Spin />
          ) : (
            <LineChartOutlined className="bmv2-empty__icon" />
          )}
          <span className="bmv2-empty__sub">
            {loading ? 'Computing benchmark return…' : message}
          </span>
          {current?.error && hasSaved ? (
            <Button size="small" onClick={() => setRetry(value => value + 1)}>
              Retry
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="bmv2-preview-chart">
          <AreaChart
            title=""
            dataSeriesName="Benchmark"
            data={data}
            additionalLines={additionalLines}
            yaxisFormatter={formatters.percent1Digit}
            xaxis={{
              type: 'datetime',
              tooltip: { enabled: false },
              labels: { datetimeUTC: true, format: 'dd MMM' },
            }}
            tooltipDateFormat="dd MMM yyyy"
            tooltip={{ shared: true, intersect: false }}
            height="100%"
            style={{ height: '100%' }}
          />
        </div>
      )}
    </SettingsCard>
  );
};

export default RealtimeBenchmarkPreviewV2;
